/**
 * Pipeline card content — entity-agnostic layout shell.
 *
 * Provides the visual structure (title row, inner content area, footer)
 * without any entity-specific logic. Entity-specific card renderers
 * (DealCardContent, GenericCardContent) compose this layout with their
 * own data extraction and formatting.
 *
 * Layout:
 * ┌─────────────────────────────────┐
 * │ (avatar) Title         Subtitle │
 * │ ┌─────────────────────────────┐ │
 * │ │       innerContent          │ │
 * │ └─────────────────────────────┘ │
 * │ ─────────────────────────────── │
 * │              footer             │
 * └─────────────────────────────────┘
 */

import { Clock } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { memo, useCallback } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

export interface PipelineCardContentProps {
  /** Primary label (left side of top row) */
  title: string;
  /** Secondary label (right side of top row, e.g. formatted amount) */
  subtitle?: string | null;
  /** Content rendered inside the inner sub-card area */
  innerContent?: ReactNode;
  /** Raw ISO date string for "Last updated" footer */
  updatedAt?: string | null;
  /** Owner display name (shown on hover of avatar) */
  ownerName?: string;
  /** Owner avatar URL */
  ownerAvatarUrl?: string;
  /** Callback when owner avatar is clicked (e.g. open slideout) */
  onOwnerClick?: () => void;
}

/**
 * Entity-agnostic pipeline card layout.
 *
 * Renders a consistent card structure that entity-specific renderers
 * (DealCardContent, GenericCardContent) fill with their own content.
 */
export const PipelineCardContent = memo(function PipelineCardContent({
  title,
  subtitle,
  innerContent,
  updatedAt,
  ownerName,
  ownerAvatarUrl,
  onOwnerClick,
}: PipelineCardContentProps) {
  const ownerInitials = ownerName
    ?.split(' ')
    .map(w => w.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleOwnerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOwnerClick?.();
    },
    [onOwnerClick]
  );

  return (
    <div>
      {/* Top row: Owner Avatar + Title + Subtitle */}
      <div className='flex items-start justify-between gap-2 px-3'>
        <div className='flex items-start gap-2 min-w-0'>
          {ownerName && (
            <Tooltip title={ownerName}>
              <button
                type='button'
                onClick={handleOwnerClick}
                className='group/avatar shrink-0 mt-0.5 cursor-pointer'
                aria-label={`View ${ownerName}`}
              >
                <span className='relative inline-flex'>
                  <Avatar
                    size='xs'
                    src={ownerAvatarUrl}
                    initials={ownerInitials}
                  />
                  <span className='absolute inset-0 rounded-full bg-black/0 group-hover/avatar:bg-black/10 transition-colors pointer-events-none' />
                </span>
              </button>
            </Tooltip>
          )}
          <span className='font-semibold text-sm text-[var(--color-text-primary)] line-clamp-2 min-w-0'>
            {title || 'Untitled'}
          </span>
        </div>
        {subtitle !== undefined && (
          <span className='text-sm font-semibold text-[var(--color-text-primary)] whitespace-nowrap flex-shrink-0'>
            {subtitle ?? '–'}
          </span>
        )}
      </div>

      {/* Inner content area — always rendered with sub-card styling */}
      {innerContent && (
        <div className='mx-3 mt-3 mb-[22px] rounded-xl border border-[var(--color-border-secondary)] dark:border-white/15 bg-[var(--color-bg-primary)] dark:bg-transparent p-5'>
          {innerContent}
        </div>
      )}

      {/* Footer: relative time, right-aligned, full-width border */}
      <div className='flex items-center justify-end gap-1 pt-1.5 pb-0.5 px-3 border-t border-[var(--color-border-secondary)] dark:border-white/15 text-xs text-[var(--color-text-tertiary)]'>
        <Clock className='size-3 flex-shrink-0' />
        <span>
          Last updated {updatedAt ? formatRelativeTime(updatedAt) : '–'}
        </span>
      </div>
    </div>
  );
});
